<template>
  <div class="success-animation-container" ref="container">
    <!-- Expanding circle splash from spinner position -->
    <div
      v-if="showCircleSplash"
      class="circle-splash"
      :class="{ expanding: isCircleExpanding }"
      :style="circlePositionStyle"
    ></div>

    <!-- Wavy splash overlay - reveals blocks animation underneath -->
    <transition name="fade">
      <div v-if="showWavySplash" class="wavy-splash-overlay">
        <div class="wave" :class="{ active: isWaveAnimating }">
          <!-- SVG wave at the leading edge (top of the wave container) -->
          <svg class="wave-svg" viewBox="0 0 1200 150" preserveAspectRatio="none">
            <path
              class="wave-path wave-path-1"
              d="M0,60 C200,0 400,150 600,60 C800,0 1000,150 1200,60 L1200,150 L0,150 Z"
              fill="#fec629"
            />
          </svg>
          <svg class="wave-svg wave-svg-2" viewBox="0 0 1200 150" preserveAspectRatio="none">
            <path
              class="wave-path wave-path-2"
              d="M0,90 C200,30 400,150 600,90 C800,30 1000,150 1200,90 L1200,150 L0,150 Z"
              fill="#fec629"
            />
          </svg>
          <!-- Yellow fill covering everything below the wave -->
          <div class="wave-fill"></div>
        </div>
      </div>
    </transition>

    <!-- Blocks animation (plays after wavy splash) -->
    <div
      ref="blocksContainer"
      class="lottie-layer blocks-layer"
      :class="{ visible: showBlocks }"
    ></div>

    <!-- Text overlay - appears after animation completes -->
    <div v-if="showText" class="text-overlay">
      <p class="amount-text">You gave S${{ formattedAmount }}</p>
      <p class="id-text">ID: {{ transactionId }}</p>
    </div>

    <!-- Sticky button at bottom -->
    <div v-if="showText" class="button-container">
      <BaseButton
        class="back-button"
        type="solid"
        size="small"
        full-width
        :solid-classes="'bg-[#42362b] border-[#42362b] text-white !rounded-[100px] h-[56px] px-[24px] py-[16px] font-bold tracking-[-0.48px] leading-[1.375rem] hover:bg-[#4F4138] hover:border-[#4F4138] active:scale-[0.98]'"
        @click="handleBackToGiving"
      >
        Back to MyGiving
      </BaseButton>
    </div>
  </div>
</template>

<script>
import lottie from 'lottie-web';
import BaseButton from '@/components/design/BaseButton.vue';

export default {
  name: 'SuccessAnimation',
  components: {
    BaseButton,
  },
  emits: ['complete'],
  props: {
    autostart: {
      type: Boolean,
      default: true,
    },
    amount: {
      type: [String, Number],
      default: '0.00',
    },
    transactionId: {
      type: [String, Number],
      default: '',
    },
    spinnerPosition: {
      type: Object,
      default: () => ({ x: '50%', y: '50%' }),
    },
  },
  data() {
    return {
      blocksAnimation: null,
      showText: false,
      showCircleSplash: false,
      isCircleExpanding: false,
      showWavySplash: false,
      isWaveAnimating: false,
      showBlocks: false,
      lottieBgColor: '#FFFFFF',
      // Track timeouts for cleanup
      timeouts: [],
      // Prevent animation from running multiple times
      hasStartedAnimation: false,
    };
  },
  computed: {
    formattedAmount() {
      // Remove commas from formatted strings (e.g., "1,500.00" → "1500.00")
      const cleanAmount = String(this.amount).replace(/,/g, '');
      const num = parseFloat(cleanAmount) || 0;
      return num.toFixed(2);
    },
    circlePositionStyle() {
      return {
        left: this.spinnerPosition.x,
        top: this.spinnerPosition.y,
      };
    },
  },
  mounted() {
    // Only start animation if autostart is true and hasn't started yet
    if (this.autostart && !this.hasStartedAnimation) {
      this.hasStartedAnimation = true;
      this.startAnimationSequence();
    }
  },
  beforeUnmount() {
    // Clear all pending timeouts
    this.timeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.timeouts = [];

    // Destroy Lottie animation
    if (this.blocksAnimation) {
      this.blocksAnimation.destroy();
      this.blocksAnimation = null;
    }

    // Reset flag for next time
    this.hasStartedAnimation = false;
  },
  methods: {
    startAnimationSequence() {
      // Phase 1: Expanding circle from spinner position (fills screen with yellow)
      this.startCircleExpansion();
    },

    startCircleExpansion() {
      // Show the circle splash
      this.showCircleSplash = true;

      // Start expansion in next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.isCircleExpanding = true;
        });
      });

      // After circle expansion completes (1s), start wavy splash reveal
      const waveTimeout = setTimeout(() => {
        this.startWavySplash();
      }, 1000);
      this.timeouts.push(waveTimeout);

      // Start loading blocks 0.3s before wavy splash (at 0.7s of circle expansion)
      const blocksTimeout = setTimeout(() => {
        this.initBlocksAnimation();
      }, 700);
      this.timeouts.push(blocksTimeout);
    },

    startWavySplash() {
      // Show the wavy splash FIRST (so it's in DOM before circle disappears)
      this.showWavySplash = true;

      // Start animation in next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // NOW hide the circle after wavy splash has rendered
          this.showCircleSplash = false;
          this.isWaveAnimating = true;
        });
      });
    },
    initBlocksAnimation() {
      // Prevent duplicate initialization
      if (this.blocksAnimation) {
        return;
      }

      const blocksContainer = this.$refs.blocksContainer;

      if (!blocksContainer) {
        return;
      }

      // Show blocks container first so Lottie can load into it
      this.showBlocks = true;

      this.blocksAnimation = lottie.loadAnimation({
        container: blocksContainer,
        renderer: 'svg',
        loop: false,
        autoplay: true,
        path: '/assets/lottie/hands-and-blocks-only.json',
        rendererSettings: {
          preserveAspectRatio: 'xMidYMid slice',
        },
      });

      let textShown = false;
      this.blocksAnimation.addEventListener('enterFrame', () => {
        const currentFrame = this.blocksAnimation.currentFrame;
        const totalFrames = this.blocksAnimation.totalFrames;
        const threeQuarterPoint = (totalFrames * 3) / 4;

        // Show text at 3/4 point
        if (currentFrame >= threeQuarterPoint && !textShown) {
          textShown = true;
          this.showText = true;
        }
      });

      this.blocksAnimation.addEventListener('complete', () => {
        this.$emit('complete');
      });
    },

    handleBackToGiving() {
      this.$router.push('/my-giving');
    },
  },
};
</script>

<style scoped>
.success-animation-container {
  position: fixed;
  inset: 0;
  z-index: 100;
  background-color: v-bind(lottieBgColor);
  overflow: hidden;
  pointer-events: none;
  transition: background-color 0.3s ease-in;
}

/* Expanding circle splash from spinner position */
.circle-splash {
  position: absolute;
  width: 0;
  height: 0;
  border-radius: 50%;
  background-color: #fec629;
  transform: translate(-50%, -50%);
  z-index: 200;
}

.circle-splash.expanding {
  animation: circleExpand 1s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
}

@keyframes circleExpand {
  to {
    width: 300vmax;
    height: 300vmax;
  }
}

/* Fade transition for overlay */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Wavy splash overlay */
.wavy-splash-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  pointer-events: none;
  overflow: hidden;
}

/* Wave container - starts at top, moves down to reveal blocks */
.wave {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: calc(100vh + 150px);
  z-index: 101;
  /* Start with wave at top (yellow visible) */
  transform: translateY(0);
}

.wave.active {
  /* Move down to reveal blocks animation underneath */
  animation: waveReveal 2s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
}

@keyframes waveReveal {
  to {
    transform: translateY(
      calc(100vh + 150px)
    ); /* Move wave completely off-screen to reveal blocks */
  }
}

/* SVG wave positioned at the leading edge (top of container) */
.wave-svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 150px;
  z-index: 2;
}

.wave-svg-2 {
  z-index: 3;
  opacity: 0.5;
}

/* Yellow fill covering everything below the wave */
.wave-fill {
  position: absolute;
  top: 150px;
  left: 0;
  width: 100%;
  height: calc(100% - 150px);
  background-color: #fec629;
  z-index: 1;
}

.lottie-layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: none;
  pointer-events: none;
}

.lottie-layer.visible {
  display: block;
}

/* Blocks layer - white background */
.blocks-layer {
  z-index: 50;
  background-color: #ffffff;
}

/* Force SVG to fill container */
.lottie-layer :deep(svg) {
  width: 100% !important;
  height: 100% !important;
  background-color: transparent !important;
}

/* Text overlay styles */
.text-overlay {
  position: absolute;
  bottom: calc(56px + 32px + 120px);
  left: 0;
  right: 0;
  text-align: center;
  z-index: 170;
  animation: fadeInUp 0.6s ease-out;
  pointer-events: auto;
}

.amount-text {
  font-size: 24px;
  font-weight: 700;
  color: #000000;
  margin-bottom: 8px;
}

.id-text {
  font-size: 18px;
  font-weight: 400;
  color: #000000;
  margin-bottom: 24px;
}

/* Sticky button container at bottom */
.button-container {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  padding: 32px 24px;
  z-index: 170;
  animation: fadeInUp 0.6s ease-out;
  pointer-events: auto;
}

.back-button {
  display: block;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
